import React, { useState } from 'react';
import {
  AppBar,
  Box,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  MenuItem,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  Chip,
} from '@mui/material';
import {
  Search,
  CalendarMonth,
  FilterList,
  DarkMode,
} from '@mui/icons-material';
import { dashboardLayoutStyles } from './dashboardLayout.styles';
import { FilterOption, SortOption, ViewMode } from '../types/dashboard.types';
import { colors } from '../../../ui/colors';

interface DashboardTopbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filterStatus: FilterOption;
  onFilterChange: (filter: FilterOption) => void;
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onThemeToggle?: () => void;
}

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'lastUpdated', label: 'Last updated' },
  { value: 'createdDate', label: 'Date range' },
  { value: 'name', label: 'Name' },
];

export const DashboardTopbar: React.FC<DashboardTopbarProps> = ({
  searchQuery,
  onSearchChange,
  filterStatus,
  onFilterChange,
  sortBy,
  onSortChange,
  viewMode,
  onViewModeChange,
  onThemeToggle,
}) => {
  const [sortAnchor, setSortAnchor] = useState<null | HTMLElement>(null);

  const handleSortClick = (e: React.MouseEvent<HTMLElement>) => {
    setSortAnchor(e.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchor(null);
  };

  const handleSortSelect = (option: SortOption) => {
    onSortChange(option);
    handleSortClose();
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: 'var(--fx-surface-base, #FFFFFF)',
        borderBottom: '1px solid var(--fx-surface-border, #E2E8F0)',
        padding: '16px 24px',
        boxShadow: 'none',
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Search Input */}
        <TextField
          placeholder="Search your sites, drafts, or folders..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search
                  sx={{
                    color: 'var(--fx-surface-texttertiary, #94A3B8)',
                    fontSize: '20px',
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            maxWidth: 400,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'var(--fx-surface-sunken, #F8FAFC)',
              borderRadius: '8px',
              fontSize: '14px',
              '& fieldset': {
                borderColor: 'var(--fx-surface-border, #E2E8F0)',
              },
              '&:hover fieldset': {
                borderColor: 'var(--fx-surface-borderstrong, #CBD5E1)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'var(--fx-surface-texttertiary, #94A3B8)',
                borderWidth: '1px',
              },
            },
            '& input': {
              padding: '8px 8px 8px 4px',
            },
            '& input::placeholder': {
              color: 'var(--fx-surface-texttertiary, #94A3B8)',
              opacity: 1,
            },
          }}
        />

        {/* Filter, Sort, and View Controls */}
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            alignItems: 'center',
          }}
        >
          {/* Status Filter Pills */}
          <Stack direction="row" spacing={0.5}>
            <Chip
              label="All"
              onClick={() => onFilterChange('all')}
              sx={{
                backgroundColor: filterStatus === 'all' ? 'var(--fx-surface-muted, #F1F5F9)' : 'transparent',
                color: filterStatus === 'all' ? 'var(--fx-surface-text, #0F172A)' : 'var(--fx-surface-textsecondary, #64748B)',
                border: 'none',
                fontSize: '14px',
                fontWeight: filterStatus === 'all' ? 500 : 400,
                height: '32px',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: 'var(--fx-surface-muted, #F1F5F9)',
                },
                '& .MuiChip-label': {
                  padding: '0 12px',
                },
              }}
            />
            <Chip
              label="Draft"
              onClick={() => onFilterChange('draft')}
              sx={{
                backgroundColor: filterStatus === 'draft' ? 'var(--fx-surface-muted, #F1F5F9)' : 'transparent',
                color: filterStatus === 'draft' ? 'var(--fx-surface-text, #0F172A)' : 'var(--fx-surface-textsecondary, #64748B)',
                border: 'none',
                fontSize: '14px',
                fontWeight: filterStatus === 'draft' ? 500 : 400,
                height: '32px',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: 'var(--fx-surface-muted, #F1F5F9)',
                },
                '& .MuiChip-label': {
                  padding: '0 12px',
                },
              }}
            />
            <Chip
              label="Published"
              onClick={() => onFilterChange('published')}
              sx={{
                backgroundColor: filterStatus === 'published' ? 'var(--fx-surface-muted, #F1F5F9)' : 'transparent',
                color: filterStatus === 'published' ? 'var(--fx-surface-text, #0F172A)' : 'var(--fx-surface-textsecondary, #64748B)',
                border: 'none',
                fontSize: '14px',
                fontWeight: filterStatus === 'published' ? 500 : 400,
                height: '32px',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: 'var(--fx-surface-muted, #F1F5F9)',
                },
                '& .MuiChip-label': {
                  padding: '0 12px',
                },
              }}
            />
          </Stack>


          {/* Sort Menu */}
          <Button
            onClick={handleSortClick}
            startIcon={<FilterList sx={{ fontSize: '18px' }} />}
            size="small"
            sx={{
              color: 'var(--fx-surface-textsecondary, #64748B)',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 400,
              minWidth: 'auto',
              padding: '6px 12px',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'var(--fx-surface-muted, #F1F5F9)',
              },
            }}
          >
            {SORT_OPTIONS.find((o) => o.value === sortBy)?.label || 'Last updated'}
          </Button>

          <Menu
            anchorEl={sortAnchor}
            open={Boolean(sortAnchor)}
            onClose={handleSortClose}
            PaperProps={{
              sx: {
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                mt: 0.5,
              },
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                selected={sortBy === option.value}
                onClick={() => handleSortSelect(option.value)}
                sx={{
                  fontSize: '14px',
                  padding: '8px 16px',
                  '&.Mui-selected': {
                    backgroundColor: 'var(--fx-surface-sunken, #F8FAFC)',
                  },
                  '&:hover': {
                    backgroundColor: 'var(--fx-surface-muted, #F1F5F9)',
                  },
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          {/* Theme Toggle */}
          {onThemeToggle && (
            <IconButton
              onClick={onThemeToggle}
              size="small"
              sx={{
                color: 'var(--fx-surface-textsecondary, #64748B)',
                padding: '6px',
                '&:hover': {
                  backgroundColor: 'var(--fx-surface-muted, #F1F5F9)',
                },
              }}
            >
              <DarkMode sx={{ fontSize: '20px' }} />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </AppBar>
  );
};